/**
 * DataQualityCard — renders the four-dimension Auto-Correctness score
 * for the authenticated user. Same component serves both landlord
 * portfolio-wide and tenant profile scoring because the backend
 * endpoint returns the same schema shape.
 *
 * Zero-network on mount: shows a shimmer skeleton then loads
 * `/api/auto/data-quality` once. Reveal signal-level detail on
 * click for auditability — the user always sees exactly which row
 * dragged their score down.
 */
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ShieldCheck, Info, Loader2, AlertCircle, Wrench } from "lucide-react";

function toneFor(score) {
  if (score >= 85) return { ring: "ring-emerald-200", text: "text-emerald-700", chip: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Excellent" };
  if (score >= 65) return { ring: "ring-amber-200",   text: "text-amber-700",   chip: "bg-amber-50 text-amber-800 border-amber-200",       label: "Needs review" };
  return                { ring: "ring-rose-200",     text: "text-rose-700",     chip: "bg-rose-50 text-rose-800 border-rose-200",           label: "Fix now" };
}

// Map data-quality dimension keys to the app route where the user
// actually fixes the underlying rows. Keeps the "Fix now" flow
// deterministic — no LLM, no guessing.
// NOTE: dimensions (accuracy / completeness / consistency / timeliness)
// are cross-cutting so they intentionally point at the dashboard —
// which is where the expanded signal list already renders. Otherwise
// a bare `/accuracy` would 404 (see support screenshot Feb 25 2026).
const FIX_ROUTE = {
  // entity keys
  properties:   "/properties",
  units:        "/properties",
  tenants:      "/tenants",
  leases:       "/leases",
  documents:    "/documents",
  applications: "/applications",
  payments:     "/payments",
  contact:      "/settings",
  profile:      "/settings",
  business:     "/settings",
  // dimension keys → land on the most-likely-to-fix entity page.
  accuracy:     "/tenants",
  completeness: "/properties",
  consistency:  "/leases",
  timeliness:   "/dashboard",
};

function bestFixRouteFor(key) {
  return FIX_ROUTE[key] || "/dashboard";
}

export default function DataQualityCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // dimension key that's open
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const worstRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setData(null); setExpanded(null); setError(false); setLoading(true);
    api.get("/auto/data-quality")
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) { setLoading(false); setError(true); } });
    return () => { alive = false; };
  }, [user?.id, user?.user_id, retry]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-3 text-sm text-slate-500" data-testid="data-quality-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your profile information…
      </div>
    );
  }
  if (error) return <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="data-quality-error" role="alert"><p className="text-sm text-slate-600">Profile information is temporarily unavailable. No score has been assumed.</p><button type="button" onClick={() => setRetry((value) => value + 1)} className="mt-3 text-sm font-semibold text-emerald-800 underline" data-testid="data-quality-retry">Retry</button></section>;
  if (!data) return null;
  if (data.state === "empty" || data.has_data === false || data.overall == null) {
    return <section className="rounded-lg border border-dashed border-slate-300 bg-white p-5 sm:p-6" data-testid="data-quality-empty">
      <h3 className="font-display text-base font-semibold text-slate-900" data-testid="data-quality-empty-title">No rental information yet</h3>
      <p className="mt-2 text-sm text-slate-600" data-testid="data-quality-empty-message">A score hasn’t been calculated. Only information saved to your account will appear here.</p>
      <Link to={user?.role === "tenant" ? "/settings" : "/properties"} className="mt-4 inline-flex text-sm font-semibold text-emerald-800 underline" data-testid="data-quality-empty-link">{user?.role === "tenant" ? "Your profile" : "Your properties"}</Link>
    </section>;
  }

  const overallTone = toneFor(data.overall);
  // The lowest-scoring dimension — that's what "Fix now" jumps to.
  const dims = data.dimensions || [];
  const worst = dims.length ? dims.slice().sort((a, b) => a.score - b.score)[0] : null;
  const handleFixNow = () => {
    if (!worst) return;
    setExpanded(worst.key);
    // Scroll the expanded signals list into view so the fix rows are
    // visible without the user having to hunt for them.
    setTimeout(() => {
      worstRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="data-quality-card">
      {/* Header — stacks on mobile, single-row on tablet+ */}
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div className={`shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full ring-2 ${overallTone.ring} bg-white flex flex-col items-center justify-center`} data-testid="data-quality-overall">
          <div className={`font-display font-extrabold text-lg sm:text-xl leading-none tabular-nums ${overallTone.text}`}>{data.overall}</div>
          <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mt-0.5">Overall</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
            <h3 className="font-display font-bold text-slate-900 text-sm sm:text-base">Auto Correctness · your data</h3>
            {data.overall < 65 && worst ? (
              <button
                type="button"
                onClick={handleFixNow}
                data-testid="data-quality-fix-now"
                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border ${overallTone.chip} hover:brightness-95 active:brightness-90 transition`}
              >
                <Wrench className="w-3 h-3" /> Fix now
              </button>
            ) : (
              <span className={`inline-flex items-center text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${overallTone.chip}`}>
                {overallTone.label}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-600 leading-relaxed">
            Four rule-based dimensions. Tap any tile to see the rows dragging that score down.
          </p>
        </div>
      </div>

      {/* Dimension grid: 2-up on phones, 2-up on tablets (portrait),
          4-up on desktop. Vertical dividers switch off on the last column
          per row to avoid dangling lines on the right edge. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-slate-100">
        {(data.dimensions || []).map((d, i) => {
          const t = toneFor(d.score);
          const isOpen = expanded === d.key;
          // Right-border only on left column in 2-col layout, hidden on
          // every column in 4-col layout except the first three.
          const rightBorder = "border-r border-slate-100 last:border-r-0 [&:nth-child(2)]:border-r-0 lg:[&:nth-child(2)]:border-r";
          const bottomBorder = i < 2 ? "border-b border-slate-100 lg:border-b-0" : "";
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => setExpanded(isOpen ? null : d.key)}
              className={`text-left p-3 sm:p-4 hover:bg-slate-50 transition min-h-[104px] ${rightBorder} ${bottomBorder} ${isOpen ? "bg-slate-50" : ""}`}
              data-testid={`data-quality-${d.key}`}
            >
              <div className="flex items-baseline gap-1.5">
                <span className={`font-display font-extrabold text-xl sm:text-2xl tabular-nums ${t.text}`}>{d.score}</span>
                <span className="text-[10px] text-slate-400">/100</span>
              </div>
              <div className="mt-0.5 text-[10px] sm:text-[11px] uppercase tracking-widest font-bold text-slate-500">{d.label}</div>
              <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed line-clamp-2 hidden sm:block">{d.definition}</p>
              {(d.signals || []).length > 0 && (
                <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-bold text-slate-500">
                  <Info className="w-3 h-3" /> {isOpen ? "Hide" : `${d.signals.length} to review`}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {expanded && (
        <div ref={worstRef} className="border-t border-slate-100 bg-amber-50/40 p-3 sm:p-4" data-testid={`data-quality-signals-${expanded}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> Rows dragging {expanded} down
            </div>
            <Link
              to={user?.role === "tenant" ? "/settings" : bestFixRouteFor(expanded)}
              data-testid={`data-quality-goto-${expanded}`}
              className="text-[11px] font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
            >
              Open {expanded} →
            </Link>
          </div>
          <ul className="space-y-1.5 text-sm text-slate-800">
            {(data.dimensions.find((d) => d.key === expanded)?.signals || ["No specific rows flagged — score is at ceiling."]).map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-slate-400 mt-0.5 shrink-0">·</span>
                <span className="leading-relaxed break-words">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-t border-slate-100 px-3 sm:px-4 py-2 text-[10px] text-slate-400 tabular-nums flex flex-wrap gap-x-3 justify-between">
        <span>Refreshed {data.generated_at ? new Date(data.generated_at).toLocaleString() : "just now"}</span>
        {typeof data.unit_count === "number" && <span>Scored across {data.unit_count} unit{data.unit_count === 1 ? "" : "s"}</span>}
      </div>
    </div>
  );
}
